import type { z } from 'zod';

import { arrayOfAll, assertNever } from '@gitbook/util-ts';

import type {
    NavigationAudienceSideSheetSchema,
    NavigationChangeRequestReviewsSideSheetSchema,
    NavigationChangeRequestSideSheetSchema,
    NavigationChangeRequestsSideSheetSchema,
    NavigationCollectionSideSheetSchema,
    NavigationCommentsSideSheetSchema,
    NavigationGitSyncSideSheetSchema,
    NavigationHistorySideSheetSchema,
    NavigationImportsSideSheetSchema,
    NavigationIntegrationsModalSchema,
    NavigationMergeRulesSideSheetSchema,
    NavigationMergeStatusSideSheetSchema,
    NavigationPageOptionsSideSheetSchema,
    NavigationPublishedSideSheetSchema,
    NavigationPublishSideSheetSchema,
    NavigationShareSideSheetSchema,
    NavigationSiteSideSheetSchema,
    NavigationSpaceSideSheetSchema,
} from './schema';
import type { NavigationScreenSpaceState } from './types';

export type NavigationChangeRequestsSideSheet = z.infer<
    typeof NavigationChangeRequestsSideSheetSchema
>;

export type NavigationGitSyncSideSheet = z.infer<typeof NavigationGitSyncSideSheetSchema>;

export type NavigationHistorySideSheet = z.infer<typeof NavigationHistorySideSheetSchema>;

export type NavigationShareSideSheet = z.infer<typeof NavigationShareSideSheetSchema>;

export type NavigationMergeRulesSideSheet = z.infer<typeof NavigationMergeRulesSideSheetSchema>;

export type NavigationMergeStatusSideSheet = z.infer<typeof NavigationMergeStatusSideSheetSchema>;

export type NavigationImportsSideSheet = z.infer<typeof NavigationImportsSideSheetSchema>;

export type NavigationCommentsSideSheet = z.infer<typeof NavigationCommentsSideSheetSchema>;

export type NavigationChangeRequestReviewsSideSheet = z.infer<
    typeof NavigationChangeRequestReviewsSideSheetSchema
>;

export type NavigationPageOptionsSideSheet = z.infer<typeof NavigationPageOptionsSideSheetSchema>;

export type NavigationAudienceSideSheet = z.infer<typeof NavigationAudienceSideSheetSchema>;

export type NavigationIntegrationsModal = z.infer<typeof NavigationIntegrationsModalSchema>;

export type NavigationPublishedSideSheet = z.infer<typeof NavigationPublishedSideSheetSchema>;

export type NavigationPublishSideSheet = z.infer<typeof NavigationPublishSideSheetSchema>;

/**
 * Sidesheets for the Space screen (not including change requests)
 */
export type NavigationSpaceSideSheet = z.infer<typeof NavigationSpaceSideSheetSchema>;

/**
 * Sidesheets for the Space screen when viewing a change request
 */
export type NavigationChangeRequestSideSheet = z.infer<
    typeof NavigationChangeRequestSideSheetSchema
>;

/**
 * Sidesheets for the Collection screen
 */
export type NavigationCollectionSideSheet = z.infer<typeof NavigationCollectionSideSheetSchema>;

/**
 * Sidesheets for the Site screen
 */
export type NavigationSiteSideSheet = z.infer<typeof NavigationSiteSideSheetSchema>;

/**
 * A utility type that represents all possible sidesheets.
 */
export type AllSideSheets =
    | NavigationSpaceSideSheet
    | NavigationChangeRequestSideSheet
    | NavigationCollectionSideSheet
    | NavigationSiteSideSheet;

/**
 * A utility type that represents all possible sidesheets with tabs.
 */
export type SideSheetsWithTabs = Extract<AllSideSheets, { tab?: string | undefined }>;

/**
 * A union of all sidesheet types.
 */
export type NavigationSideSheetType = AllSideSheets['type'];

/**
 * Returns the keys of all possible tabs for a given sidesheet type.
 */
export function getNavigationSideSheetTabs(
    type: NavigationSideSheetType
): TabKeys<AllSideSheets['tab']>[] {
    switch (type) {
        case 'gitsync':
            return arrayOfAll<TabKeys<NavigationGitSyncSideSheet['tab']>>()(['setup', 'status']);
        case 'share':
            return arrayOfAll<TabKeys<NavigationShareSideSheet['tab']>>()(['invite', 'docs-sites']);
        case 'page':
            return arrayOfAll<TabKeys<NavigationPageOptionsSideSheet['tab']>>()([
                'layout',
                'visibility',
                'metadata',
            ]);
        case 'change-requests':
        case 'comments':
        case 'audience':
        case 'published':
        case 'publish':
        case 'merge-rule':
        case 'merge-status':
        case 'integrations':
        case 'imports':
        case 'history':
            return [];
        default:
            assertNever(type);
    }
}

/**
 * The keys of all possible sidesheets in a change request screen.
 */
const CHANGE_REQUEST_SIDESHEET_KEYS = arrayOfAll<NavigationChangeRequestSideSheet['type']>()([
    'comments',
    'page',
    'share',
    'integrations',
    'merge-rule',
    'merge-status',
    'imports',
    'history',
]);

/**
 * The keys of all possible sidesheets in a space screen.
 */
const SPACE_SIDESHEET_KEYS = arrayOfAll<NavigationSpaceSideSheet['type']>()([
    'change-requests',
    'gitsync',
    'share',
    'comments',
    'page',
    'integrations',
    'merge-rule',
    'imports',
    'history',
]);

/**
 * Get the keys of all possible sidesheets in a space screen
 */
export function getSpaceSideSheetKeys(
    screen: NavigationScreenSpaceState
): Array<NavigationSpaceSideSheet['type'] | NavigationChangeRequestSideSheet['type']> {
    if (screen.revision) {
        return ['history'];
    }

    return (screen.changeRequest ? CHANGE_REQUEST_SIDESHEET_KEYS : SPACE_SIDESHEET_KEYS).filter(
        (sideSheet) => sideSheet !== 'history'
    );
}

/**
 * The keys of all possible sidesheets in a collection screen.
 */
export const COLLECTION_SIDESHEET_KEYS = arrayOfAll<NavigationCollectionSideSheet['type']>()([
    'share',
]);

export const SITE_SIDESHEET_KEYS = arrayOfAll<NavigationSiteSideSheet['type']>()([
    'audience',
    'gitsync',
    'integrations',
    'published',
    'publish',
]);

/**
 * The keys of all possible sidesheets.
 */
export const ALL_SIDESHEET_KEYS = arrayOfAll<AllSideSheets['type']>()([
    ...SITE_SIDESHEET_KEYS,
    ...SPACE_SIDESHEET_KEYS,
    ...CHANGE_REQUEST_SIDESHEET_KEYS,
    ...COLLECTION_SIDESHEET_KEYS,
]);

/**
 * SideSheet navigation state allows for "tab" to be undefined. This utility type
 * can be used to remove undefined from the ScreenState['tab'] type.
 */
export type TabKeys<Keys> = Exclude<Keys, undefined>;
